/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import { PriceColumn, PriceCalculation as PricingType } from '@idearoom/types';
import { ComponentCategoryKey, SizeBasedCategoryKey } from './ClientUpdateCategoryKey';
import DoorCategoryImage from '../images/door-category.png';
import GarageCategoryImage from '../images/garage-category.png';
import FrameoutCategoryImage from '../images/frameout-category.png';
import WindowCategoryImage from '../images/window-category.png';
import TransomCategoryImage from '../images/transom-category.png';
import DormerCategoryImage from '../images/dormer-category.png';
import CupolaCategoryImage from '../images/cupola-category.png';
import ShutterCategoryImage from '../images/shutter-category.png';
import FlowerBoxCategoryImage from '../images/flowerbox-category.png';
import VentCategoryImage from '../images/vent-category.png';
import RampCategoryImage from '../images/ramp-category.png';
import ShelfCategoryImage from '../images/shelf-category.png';
import WorkbenchCategoryImage from '../images/workbench-category.png';
import FlooringCategoryImage from '../images/flooring-category.png';
import SidingCategoryImage from '../images/siding-category.png';
import NestingBoxCategoryImage from '../images/nestingbox-category.png';
import LegHeightsCategoryImage from '../images/legheights-category.png';
import GableEndImage from '../images/gable-end.png';
import EndEnclosedImage from '../images/end-enclosed.png';
import SideEnclosedImage from '../images/side-enclosed.png';
import SidePanelsImage from '../images/side-panels.png';
import { I18nKeys } from './I18nKeys';
import { ClientUpdateCategoryKey, ComponentCategoryItem, rowGroups } from '../types/PricingClientUpdate';
import { PricingComponentEditFields } from './FormFields';

export enum MiscPriceColumns {
  UpgradePrice = 'upgradePrice',
}

export enum PricingCalculationColumns {
  PriceCalculation = 'priceCalculation',
}

export enum DisplayColumns {
  Label = 'label',
  PriceExpression = 'priceExpression',
}

export enum ComponentAttributeColumns {
  WallPosition = 'wallPosition',
  WidthTags = 'widthTags',
  Height = 'height',
  Width = 'width',
  CustomExpression = 'customExpression',
  StyleKey = 'styleKey',
}

export enum MiscProperties {
  LegLabel = 'legLabel',
}

export enum HelperColumns {
  Key = 'key',
  VariesByRegion = 'variesByRegion',
  AppliesTo = 'appliesTo',
}

export enum ActionColumn {
  Edit = 'edit',
}

export enum PricingSheetHeaders {
  PricingSheetLabel = 'pricingSheetLabel',
  PriceRange = 'priceRange',
  HeightRange = 'heightRange',
}

export enum PricingSizeBasedAttributes {
  SingleSlope = 'singleSlope',
  Leg = 'leg',
  WidthTags = 'widthTags',
  CustomExpression = 'customExpression',
}

export enum PriceByQuantity {
  Each = 'each',
  Combined = 'combined',
  Split = 'split',
}

export enum SidingDirection {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export const pricingComponentWidthProps: {
  [column: string]: { minWidth?: number; width?: number; maxWidth?: number };
} = {
  [DisplayColumns.Label]: { minWidth: 324 },
  [PriceColumn.price]: { minWidth: 78 },
  [PricingCalculationColumns.PriceCalculation]: { minWidth: 128 },
  [DisplayColumns.PriceExpression]: { minWidth: 128 },
  [HelperColumns.AppliesTo]: { minWidth: 160 },
  [MiscPriceColumns.UpgradePrice]: { maxWidth: 78 },
  [HelperColumns.VariesByRegion]: { width: 90, maxWidth: 88 },
  [PriceColumn.region1]: { maxWidth: 78 },
  [PriceColumn.region2]: { maxWidth: 78 },
  [PriceColumn.region3]: { maxWidth: 78 },
  [PriceColumn.region4]: { maxWidth: 78 },
  [PriceColumn.region5]: { maxWidth: 78 },
  [PriceColumn.region6]: { maxWidth: 78 },
  [PriceColumn.region7]: { maxWidth: 78 },
  [ActionColumn.Edit]: { minWidth: 44, width: 77 },
};

// Use to associate columns with order to be displayed in item table and price calculation fields
export const componentColumnMap: {
  [pricingField: string]: { calculation?: PricingCalculationColumns; order: number };
} = {
  [DisplayColumns.Label]: { order: 1 },
  [PriceColumn.price]: { calculation: PricingCalculationColumns.PriceCalculation, order: 2 },
  [PricingCalculationColumns.PriceCalculation]: { order: 3 },
  [MiscPriceColumns.UpgradePrice]: { order: 4 },
  [DisplayColumns.PriceExpression]: { order: 5 },
  [HelperColumns.VariesByRegion]: { order: 6 },
  [HelperColumns.AppliesTo]: { order: 8 },
  [PriceColumn.region1]: { order: 9 },
  [PriceColumn.region2]: { order: 10 },
  [PriceColumn.region3]: { order: 11 },
  [PriceColumn.region4]: { order: 12 },
  [PriceColumn.region5]: { order: 13 },
  [PriceColumn.region6]: { order: 14 },
  [PriceColumn.region7]: { order: 15 },
  [ActionColumn.Edit]: { order: 100 },
};

export interface ComponentFormData {
  [PricingComponentEditFields.Component]: string;
  [PricingComponentEditFields.Table]: string;
  [DisplayColumns.Label]: string;
  [PriceColumn.price]?: string;
  [PriceColumn.region1]?: string;
  [PriceColumn.region2]?: string;
  [PriceColumn.region3]?: string;
  [PriceColumn.region4]?: string;
  [PriceColumn.region5]?: string;
  [PriceColumn.region6]?: string;
  [PriceColumn.region7]?: string;
  [PricingCalculationColumns.PriceCalculation]?: string;
  [MiscPriceColumns.UpgradePrice]?: string;
  [DisplayColumns.PriceExpression]?: string;
  [HelperColumns.VariesByRegion]: boolean;
  [HelperColumns.Key]: string;
}

export const CLIENT_UPDATE_CATEGORY_MAPPING: {
  [key in ClientUpdateCategoryKey]: {
    label: string;
    image?: string;
    defaultPricingType?: PricingType;
    rowGroups?: (keyof ComponentCategoryItem)[];
  };
} = {
  [ComponentCategoryKey.Door]: {
    label: I18nKeys.PricingClientUpdateCategoryDoors,
    image: DoorCategoryImage,
  },
  [ComponentCategoryKey.GarageDoor]: {
    label: I18nKeys.PricingClientUpdateCategoryGarageDoors,
    image: GarageCategoryImage,
  },
  [ComponentCategoryKey.Frameout]: {
    label: I18nKeys.PricingClientUpdateCategoryFrameouts,
    image: FrameoutCategoryImage,
    rowGroups: [DisplayColumns.Label, ...Object.values(rowGroups)],
  },
  [ComponentCategoryKey.FrameoutCorner]: {
    label: I18nKeys.PricingClientUpdateCategoryFrameoutCorners,
    image: FrameoutCategoryImage,
    rowGroups: [DisplayColumns.Label, ...Object.values(rowGroups)],
  },
  [ComponentCategoryKey.Window]: {
    label: I18nKeys.PricingClientUpdateCategoryWindows,
    image: WindowCategoryImage,
  },
  [ComponentCategoryKey.WindowTransom]: {
    label: I18nKeys.PricingClientUpdateCategoryWindowsTransom,
    image: TransomCategoryImage,
  },
  [ComponentCategoryKey.Dormer]: {
    label: I18nKeys.PricingClientUpdateCategoryDormers,
    image: DormerCategoryImage,
  },
  [ComponentCategoryKey.Cupola]: {
    label: I18nKeys.PricingClientUpdateCategoryCupolas,
    image: CupolaCategoryImage,
  },
  [ComponentCategoryKey.Shutter]: {
    label: I18nKeys.PricingClientUpdateCategoryShutters,
    image: ShutterCategoryImage,
  },
  [ComponentCategoryKey.FlowerBox]: {
    label: I18nKeys.PricingClientUpdateCategoryFlowerBoxes,
    image: FlowerBoxCategoryImage,
  },
  [ComponentCategoryKey.NestingBox]: {
    label: I18nKeys.PricingClientUpdateCategoryNestingBoxes,
    image: NestingBoxCategoryImage,
    rowGroups: [DisplayColumns.Label, ...Object.values(rowGroups)],
  },
  [ComponentCategoryKey.Vent]: {
    label: I18nKeys.PricingClientUpdateCategoryVents,
    image: VentCategoryImage,
  },
  [ComponentCategoryKey.Ramp]: {
    label: I18nKeys.PricingClientUpdateCategoryRamps,
    image: RampCategoryImage,
  },
  [ComponentCategoryKey.Shelf]: {
    label: I18nKeys.PricingClientUpdateCategoryShelves,
    image: ShelfCategoryImage,
  },
  [ComponentCategoryKey.Workbench]: {
    label: I18nKeys.PricingClientUpdateCategoryWorkbenches,
    image: WorkbenchCategoryImage,
  },
  [ComponentCategoryKey.Flooring]: {
    label: I18nKeys.PricingClientUpdateCategoryFlooring,
    image: FlooringCategoryImage,
    defaultPricingType: PricingType.PerSquareFoot,
    rowGroups: [DisplayColumns.Label, ...Object.values(rowGroups)],
  },
  [ComponentCategoryKey.Siding]: {
    label: I18nKeys.PricingClientUpdateCategorySiding,
    image: SidingCategoryImage,
  },
  [SizeBasedCategoryKey.EndWalls]: {
    label: I18nKeys.PricingClientUpdateCategoryEndWalls,
    image: EndEnclosedImage,
  },
  [SizeBasedCategoryKey.Insulation]: {
    label: I18nKeys.PricingClientUpdateCategoryInsulation,
    image:
      'https://images-wixmp-ed30a86b8c4ca887773594c2.wixmp.com/f/aaa79357-9476-4c1d-b9a8-6e881f2449d3/ddgdrbe-12584b9d-2ee7-4540-984a-fca6876b55f9.png?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ1cm46YXBwOjdlMGQxODg5ODIyNjQzNzNhNWYwZDQxNWVhMGQyNmUwIiwiaXNzIjoidXJuOmFwcDo3ZTBkMTg4OTgyMjY0MzczYTVmMGQ0MTVlYTBkMjZlMCIsIm9iaiI6W1t7InBhdGgiOiJcL2ZcL2FhYTc5MzU3LTk0NzYtNGMxZC1iOWE4LTZlODgxZjI0NDlkM1wvZGRnZHJiZS0xMjU4NGI5ZC0yZWU3LTQ1NDAtOTg0YS1mY2E2ODc2YjU1ZjkucG5nIn1dXSwiYXVkIjpbInVybjpzZXJ2aWNlOmZpbGUuZG93bmxvYWQiXX0.QN7lda26ydmuCTLJdK66DBbj8zZf6KW0rjr4Wqfrtog',
  },
  [SizeBasedCategoryKey.LegHeight]: {
    label: I18nKeys.PricingClientUpdateCategoryLegs,
    image: LegHeightsCategoryImage,
  },
  [SizeBasedCategoryKey.Panels]: {
    label: I18nKeys.PricingClientUpdateCategoryPanels,
    image: SidePanelsImage,
  },
  [SizeBasedCategoryKey.GableWalls]: {
    label: I18nKeys.PricingClientUpdateCategoryGableWalls,
    image: GableEndImage,
  },
  [SizeBasedCategoryKey.SideWalls]: {
    label: I18nKeys.PricingClientUpdateCategorySideWalls,
    image: SideEnclosedImage,
  },
  [SizeBasedCategoryKey.Roof]: {
    label: I18nKeys.PricingClientUpdateCategoryRoof,
    image:
      'https://images-wixmp-ed30a86b8c4ca887773594c2.wixmp.com/f/aaa79357-9476-4c1d-b9a8-6e881f2449d3/ddgdrbe-12584b9d-2ee7-4540-984a-fca6876b55f9.png?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ1cm46YXBwOjdlMGQxODg5ODIyNjQzNzNhNWYwZDQxNWVhMGQyNmUwIiwiaXNzIjoidXJuOmFwcDo3ZTBkMTg4OTgyMjY0MzczYTVmMGQ0MTVlYTBkMjZlMCIsIm9iaiI6W1t7InBhdGgiOiJcL2ZcL2FhYTc5MzU3LTk0NzYtNGMxZC1iOWE4LTZlODgxZjI0NDlkM1wvZGRnZHJiZS0xMjU4NGI5ZC0yZWU3LTQ1NDAtOTg0YS1mY2E2ODc2YjU1ZjkucG5nIn1dXSwiYXVkIjpbInVybjpzZXJ2aWNlOmZpbGUuZG93bmxvYWQiXX0.QN7lda26ydmuCTLJdK66DBbj8zZf6KW0rjr4Wqfrtog',
  },
};

export const PRICING_TYPE_MAP: {
  [key in PricingType]?: I18nKeys;
} = {
  [PricingType.Amount]: I18nKeys.PricingComponentPricingTypeAmount,
};
